// src/views/examples/Report.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  ModalFooter,
} from "reactstrap";
import Header from "components/Headers/Header";
import Swal from "sweetalert2";
import axios from "axios";
import { useSessionStore } from "../stores/sessionStore";

// ✅ BASE จาก env
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const API_REPORTS = `${API_BASE}/reports`;

// ✅ ค่าคงที่ที่ต้องส่งไป backend (ผู้ใช้ไม่ต้องกรอก)
const FIXED_REPORT = {
  reportKey: "sales_summary",
  reportPath: "/reports/sales/summary",
  templateKey: "tpl_sales_v1",
  dataSourceKey: "ds_customer_main",
  exportModes: "PDF,EXCEL",
  isActive: true,
  settings: {
    paramSchemaJson:
      "{\"type\":\"object\",\"properties\":{\"dateFrom\":{\"type\":\"string\"}}}",
    defaultConfigJson: "{\"pageSize\":50}",
  },
};

const Report = () => {
  const navigate = useNavigate();
  const actor = useSessionStore((s) => s.profile?.username);

  const [submitting, setSubmitting] = useState(false);

  // ✅ user input only
  const [reportName, setReportName] = useState("");

  // ✅ Custom Fields -> columnsJson
  // [{ id, title }]
  const [fields, setFields] = useState([]);

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), title: "" },
    ]);
  };

  const updateField = (id, value) => {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, title: value } : f)),
    );
  };

  const removeField = (id) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  };

  const handleCancel = () => navigate("/reports");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      if (!API_BASE) throw new Error("ไม่ได้ตั้งค่า REACT_APP_API_BASE ในไฟล์ .env");
      if (!actor) throw new Error("ไม่พบ username ผู้สร้าง (x-actor)");
      if (!reportName.trim()) throw new Error("กรุณากรอก Report Name");

      // ✅ build columnsJson: auto key col1..n (ตามลำดับที่กรอก)
      const columnsArr = fields
        .map((f, idx) => ({
          key: `col${idx + 1}`,
          title: String(f.title || "").trim(),
        }))
        .filter((x) => x.title); // ข้ามช่องว่าง

      const payload = {
        ...FIXED_REPORT,
        reportName: reportName.trim(),
        settings: {
          ...FIXED_REPORT.settings,
          columnsJson: JSON.stringify(columnsArr),
        },
      };

      setSubmitting(true);

      const res = await axios.post(API_REPORTS, payload, {
        headers: {
          "Content-Type": "application/json",
          "x-actor": actor,
        },
      });

      const ok =
        res?.data?.ok === true ||
        String(res?.data?.statustext || "").toUpperCase() === "SUCCESS";

      if (!ok) throw new Error(res?.data?.message || "บันทึกรายงานไม่สำเร็จ");

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: res?.data?.message || "บันทึกรายงานสำเร็จ",
        showConfirmButton: false,
        timer: 1200,
      });

      navigate("/reports");
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || err?.message || "เกิดข้อผิดพลาด",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h6 className="mb-0">Create Report</h6>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form autoComplete="off" onSubmit={handleSubmit}>
                  <div className="pl-lg-4">
                    {/* ✅ Report Name */}
                    <Row>
                      <Col lg="8">
                        <FormGroup>
                          <label className="form-control-label required">
                            Report Name
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            value={reportName}
                            onChange={(e) => setReportName(e.target.value)}
                            placeholder='เช่น "รายงานยอดขาย"'
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* ✅ Custom Fields -> columnsJson */}
                    <Row className="mt-3">
                      <Col
                        lg="12"
                        className="d-flex justify-content-between align-items-center"
                      >
                        <h6 className="heading-small text-muted mb-0">
                          Custom Fields (columnsJson)
                        </h6>

                        <Button
                          type="button"
                          color="info"
                          size="md"
                          onClick={addField}
                          disabled={submitting}
                          style={{ borderRadius: 999 }}
                        >
                          + Add Field
                        </Button>
                      </Col>
                    </Row>

                    {fields.length > 0 && (
                      <Row className="mt-3">
                        <Col lg="12">
                          {fields.map((f, idx) => (
                            <Row key={f.id} className="align-items-end">
                              <Col md="11">
                                <FormGroup className="mb-2">
                                  <label className="form-control-label">
                                    Field {idx + 1} (auto key: col{idx + 1})
                                  </label>
                                  <Input
                                    type="text"
                                    placeholder='เช่น "สินค้า"'
                                    value={f.title}
                                    onChange={(e) => updateField(f.id, e.target.value)}
                                  />
                                </FormGroup>
                              </Col>

                              <Col md="1" className="d-flex align-items-end">
                                <Button
                                  type="button"
                                  color="link"
                                  className="p-0"
                                  onClick={() => removeField(f.id)}
                                  title="ลบ field นี้"
                                >
                                  ✕
                                </Button>
                              </Col>
                            </Row>
                          ))}
                        </Col>
                      </Row>
                    )}

                    <ModalFooter className="px-0 d-flex justify-content-end align-items-center flex-wrap gap-2">
                      <div>
                        <Button color="primary" disabled={submitting} type="submit">
                          {submitting ? "Submitting..." : "Submit"}
                        </Button>
                      </div>
                    </ModalFooter>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Report;
